import {
  Chip as MuiChip,
  ChipOwnProps,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useProductsTFunction } from '@retail/products/i18n';
import { getLLDate } from '@retail/utils';
import { CloseIcon } from '@sanity/icons';

interface Props {
  tagName: string;
  dateRange?: { fromDate: Date; toDate?: Date };
  hasTooltip?: boolean;
  /** Show the whole tag name instead of shortening it to fit a narrow column. */
  fullLabel?: boolean;
  color?: ChipOwnProps['color'];
  removeChip?: () => void;
}

export function AssortmentTagChip({
  tagName,
  dateRange,
  hasTooltip,
  fullLabel,
  color,
  removeChip,
}: Props) {
  const t = useProductsTFunction();

  if (!hasTooltip)
    return <Chip label={tagName} color={color} fullLabel={fullLabel} onRemove={removeChip} />;

  return (
    <Tooltip
      title={
        <Stack flexDirection="column" spacing={1}>
          <Typography variant="body2">{tagName}</Typography>
          {dateRange && (
            <>
              <Stack flexDirection="row" alignItems="center" gap={0.5}>
                <Typography variant="body3">{t('products.mgItem.fromDate.label')}:</Typography>
                <Typography variant="body3">{getLLDate(dateRange.fromDate)}</Typography>
              </Stack>
              <Stack flexDirection="row" alignItems="center" gap={0.5}>
                <Typography variant="body3">{t('products.mgItem.toDate.label')}:</Typography>
                <Typography variant="body3">{getLLDate(dateRange.toDate) ?? '-'}</Typography>
              </Stack>
            </>
          )}
        </Stack>
      }
    >
      <Stack>
        <Chip label={tagName} color={color} fullLabel={fullLabel} onRemove={removeChip} />
      </Stack>
    </Tooltip>
  );
}

interface ChipProps {
  label: string;
  color?: ChipOwnProps['color'];
  fullLabel?: boolean;
  onRemove?: () => void;
}

function Chip({ label, color, fullLabel, onRemove }: ChipProps) {
  return (
    <MuiChip
      color={color}
      sx={{ height: 24, mr: 0.3, '& .MuiChip-label': { px: 0.8 } }}
      label={
        <Stack flexDirection="row" alignItems="center" gap={0.5}>
          {fullLabel ? label : label?.slice(0, 4)}
          {onRemove && (
            <IconButton sx={{ p: 0 }} onClick={onRemove}>
              <CloseIcon fontSize="medium" />
            </IconButton>
          )}
        </Stack>
      }
    />
  );
}
